import { FeatureFlags } from '@dupe/common/constants'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/src/components/ui/tooltip'
import { cn } from '~/src/lib/utils'

interface Props {
  /** Whether the underlying row was AMP-monetized (link swapped to AMP
   *  click_url). Caller passes `match.monetized === 'amp'`. */
  monetized?: boolean
  className?: string
}

/**
 * Internal-only visual marker for AMP-monetized Amazon rows. Renders only
 * when the `amp-amazon-monetized-badge` PostHog flag is enabled for the
 * current user. PostHog flag targeting handles the internal-users-only
 * scope — we don't gate it in code so support can flip it for adhoc QA.
 */
const AmpMonetizedBadge: React.FC<Props> = ({ monetized, className }) => {
  const { featureOn } = useIsFeatureOn(FeatureFlags.AmpAmazonMonetizedBadge)
  if (!monetized || !featureOn) return null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              // Use the semantic `success-500` token per the frontend
              // color-token policy (docs/references/frontend-color-token-policy.md).
              'inline-flex items-center justify-center rounded-sm bg-success-500 px-1 py-0 text-[10px] font-semibold uppercase leading-4 text-white',
              className
            )}
            aria-label='AMP monetized'
          >
            $
          </span>
        </TooltipTrigger>
        <TooltipContent className='w-60 normal-case'>
          <p>
            This Amazon row's click-out was swapped to adMarketplace AMP
            (monetized).
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default AmpMonetizedBadge
